import { type ComponentType, useMemo } from 'react';
import type { GroupBase, MultiValue, StylesConfig } from 'react-select';
import Select from 'react-select';

import { useDeviceModels, useIntl } from '../../hooks';

type SelectProps = React.ComponentProps<typeof Select<ModelOption, true, GroupBase<ModelOption>>>;

interface ModelOption {
  value: string;
  label: string;
}

const SelectComponent = Select as unknown as ComponentType<SelectProps>;

const selectStyles: StylesConfig<ModelOption, true, GroupBase<ModelOption>> = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 40,
    borderRadius: 6,
    borderColor: 'var(--color-border)',
    color: 'var(--color-foreground)',
    ':hover': {
      borderColor: 'var(--color-border)',
      cursor: 'pointer',
    },
    backgroundColor: state.isDisabled ? 'var(--color-background-dark)' : 'var(--color-input)',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 30,
    backgroundColor: 'var(--color-background-light)',
    border: '1px solid var(--color-border)',
  }),
  option: (provided) => ({
    ...provided,
    backgroundColor: 'var(--color-background-light)',
    color: 'var(--color-foreground)',
    ':hover': {
      backgroundColor: 'var(--color-background-dark)',
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'var(--color-foreground)',
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: 'var(--color-background-dark)',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: 'var(--color-foreground)',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: 'var(--color-foreground)',
    ':hover': {
      backgroundColor: 'var(--color-destructive)',
      color: 'white',
    },
  }),
};

interface DeviceModelsSelectProps {
  value: string[];
  onChange: (models: string[]) => void;
  disabled?: boolean;
}

export function DeviceModelsSelect({ value, onChange, disabled = false }: DeviceModelsSelectProps) {
  const intl = useIntl();
  const { data: deviceModels = [], isLoading } = useDeviceModels();

  const options: ModelOption[] = useMemo(
    () => deviceModels.map((model) => ({ value: model, label: model })),
    [deviceModels],
  );

  const selectedOptions: ModelOption[] = useMemo(
    () => value.map((model) => ({ value: model, label: model })),
    [value],
  );

  const handleChange = (newValue: MultiValue<ModelOption>) => {
    onChange(newValue.map((option) => option.value));
  };

  return (
    <SelectComponent
      isMulti
      value={selectedOptions}
      onChange={handleChange}
      options={options}
      isLoading={isLoading}
      isDisabled={disabled}
      placeholder={intl.formatMessage({ id: 'firmwares.deviceModelsPlaceholder' })}
      noOptionsMessage={() => intl.formatMessage({ id: 'firmwares.noDeviceModels' })}
      classNamePrefix="device-models-select"
      styles={selectStyles}
      inputId="device-models-select"
      instanceId="device-models-select"
    />
  );
}
